import { useEffect, useMemo, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { Table } from 'shared/ui/TableNew'
import { SpinnerLoader } from 'shared/ui'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type RowContact } from 'entities/Contacts/model/Table/RowContact'
import { contactsDetailsGlobalStore } from 'widgets/ContactsDetails/store'
import { type FilterItemConfig } from 'widgets/FilterEditor/model/FilterItemConfig.ts'
import { LeftContent } from 'pages/contacts/ui/ContactsTable/ui/LeftContent/LeftContent'
import { useContactsTableContext } from 'pages/contacts/ui/ContactsTable/context/contactsTableContext'
import { columnTransformer } from '../helpers/helpers'
import { ContactTableFilterDropdown } from './TableActions/ContactTableFilterDropdown'
import { ExportButton } from './TableActions/ExportButton'
import styles from './styles.module.scss'
import { getContactsActions } from '../helpers/contactsActions'

const pinnedColumnNames = ['name']

const ActionsRight = () => (
  <>
    <ExportButton />
    <ContactTableFilterDropdown />
  </>
)

export const ContactsTableContent = observer(() => {
  const contactsTableStore = useContactsTableContext()
  const entities = useEntitiesContext()
  const {
    contactsRows,
    isNewContactMode,
    activeContact,
    inActionId,
    loadContacts,
    handleChangePagination,
    handleTableWidth,
    setActiveContact,
    resetNewContactMode,
    resetPagination,
    tableStore,
    filterGroups,
    filterConfig,
  } = contactsTableStore

  const { isCollapse, handleChangeCollapse } = contactsDetailsGlobalStore
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const { user } = entities.usersStore

  useEffect(() => {
    contactsTableStore.reactionFilters()

    return resetPagination
  }, [])

  const rows = useMemo(
    () =>
      contactsRows.map((row) => {
        row.setActionsProps({
          showItems: 0,
          actions: getContactsActions(row, contactsTableStore),
        })

        row.setAriaLabel('ContactItemRow')

        return row
      }),
    [contactsRows]
  )

  const handleResize = () => {
    const width = tableContainerRef.current?.clientWidth || null

    if (!width) return

    const actualWidth = width > 585 ? width : 585

    handleTableWidth(actualWidth)
  }

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [filterGroups.length])

  if (!user) return null

  const { activeFields } = user

  if (!activeFields || !filterGroups.length) {
    return <SpinnerLoader ariaLabel={'ContactsTableContent'} />
  }

  const filtersList = {
    customFilters: activeFields.reduce<FilterItemConfig[]>((state, key) => {
      const item = filterConfig?.getCustomFilter(key)
      if (item) state.push(item)
      return state
    }, []),
    filters: activeFields.reduce<FilterItemConfig[]>((state, key) => {
      const item = filterConfig?.getFilter(key)
      if (item) state.push(item)
      return state
    }, []),
  }

  const onPaginationModelChange = async (page: number, pageSize: number) => {
    handleChangePagination(page, pageSize)
    setActiveContact(null)
    await loadContacts()
  }

  const columns = columnTransformer(
    filtersList,
    activeFields,
    contactsTableStore.onOpenContactsDetails
  )

  const onRowClick = (row: RowContact) => {
    if (row.isDisabled || row.origin?.isDisabled) return

    const newContact = row.id === -1

    !newContact && setActiveContact(row.origin)
    resetNewContactMode()

    isCollapse && handleChangeCollapse(false)
  }

  return (
    <div className={styles.tableContainer}>
      <div className={styles.table} ref={tableContainerRef}>
        <Table
          ariaLabel={'ContactsTableContent'}
          store={tableStore}
          isCheckDate
          withCheckbox
          withSelectAll
          columns={columns}
          rows={rows}
          activeRowId={activeContact?.id}
          inActionId={inActionId}
          pinnedColumnIds={pinnedColumnNames}
          headerLeftContentProps={{
            actionsContent: <LeftContent />,
          }}
          rightHeaderContent={<ActionsRight />}
          hidePagination={isNewContactMode}
          onChangePagination={onPaginationModelChange}
          onRowClick={onRowClick}
        />
      </div>
    </div>
  )
})
